import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { EvaInputComponent } from "src/app/shared/ds/eva-input/eva-input.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EMPTY, Subject } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";

import { EvaService } from "src/app/core/services/eva.service";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";
import { NoteService } from "src/app/services/note.service";
import { AttachmentSearchGroup } from "src/app/domains/note";

import { type TagPreset } from "src/app/shared/constants/tag-presets";

import { Task, TaskUrgency, TaskObjectLink } from "../domains/task";
import { defaultAssigneeFor } from "../domains/default-assignee";
import { TaskService } from "../services/task.service";
import { AssigneeItem } from "../task-assignee-picker/task-assignee-picker.component";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";

@Component({
  selector: "app-task-inline-create",
  templateUrl: "./task-inline-create.component.html",
  styleUrls: ["./task-inline-create.component.scss"],
})
export class TaskInlineCreateComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);

  /** Date d'échéance pré-remplie (ex: jour courant dans la vue Aujourd'hui) */
  @Input() defaultDate: Date = null;
  /** Assigné par défaut (ex: utilisateur courant) */
  @Input() defaultAssignee: AssigneeItem | null = null;
  /** Liste des employés pour l'assignation */
  @Input() employees: any[] = [];
  /** Liens objets pré-remplis (ex: client lié à la tâche) */
  @Input() defaultObjectsLinks: TaskObjectLink[] = [];
  /** Contrôle la visibilité depuis le parent */
  @Input() visible = false;
  /** Si false, le bouton trigger est masqué (utilisé quand un bouton externe pilote l'ouverture) */
  @Input() showTrigger = true;

  /** Si true, le formulaire reste ouvert après création (reset + focus sur le titre) */
  @Input() stayOpenAfterCreate: boolean = false;
  /** Créée depuis la fiche d'un objet : le lien vers cet objet est implicite, « Lier à » et la pastille sont masqués */
  @Input() hideObjectLinks: boolean = false;

  /** Émis après création réussie d'une tâche */
  @Output() taskCreated = new EventEmitter<Task>();
  /** Émis quand l'utilisateur annule */
  @Output() cancelled = new EventEmitter<void>();
  /** Two-way binding sur visible */
  @Output() visibleChange = new EventEmitter<boolean>();

  @ViewChild("titleInput") titleInput: EvaInputComponent;

  // Champs du formulaire
  title = "";
  description = "";
  dueDate: Date = null;
  plannedStartDate: Date = null;
  urgency: TaskUrgency = TaskUrgency.NORMAL;
  isPrivate = false;
  assignedTo: AssigneeItem[] = [];

  // Mode date
  dateMode: "none" | "deadline" | "range" = "none";

  readonly dateModeOptions = [
    { label: "Aucune", value: "none" },
    { label: "Échéance", value: "deadline" },
    { label: "Période", value: "range" },
  ];

  /** Copie mutable des liens objets (permet le dismiss par l'utilisateur) */
  activeObjectsLinks: TaskObjectLink[] = [];

  // État
  saving = false;
  titleError = "";
  showDescription = false;

  // Lier à
  showLinkSearch = false;
  linkSearchQuery = "";
  linkSearchResults: AttachmentSearchGroup[] = [];
  isSearchingLinks = false;
  private linkSearch$ = new Subject<string>();

  private elementRef = inject(ElementRef);
  private noteService = inject(NoteService);

  /**
   * Échap ferme d'abord le menu ouvert (« Lier à », calendrier) : marqué traité, le panneau ou la
   * modale autour reste ouvert. Le focus revient au titre, sinon l'Échap suivant fermerait tout.
   */
  @HostListener("keydown.escape", ["$event"])
  onMenuEscape(event: KeyboardEvent): void {
    if (event.defaultPrevented) {
      // Le calendrier vient de se fermer lui-même (déjà retiré du DOM : composedPath garde le chemin d'origine)
      const fromDatePicker = event.composedPath().some((el) => (el as HTMLElement).tagName === "APP-TASK-DATE-PICKER");
      if (fromDatePicker) this.focusTitle();
      return;
    }
    if (!this.showLinkSearch) return;
    event.preventDefault();
    this.toggleLinkSearch();
    this.focusTitle();
  }

  onDocumentClick(event: Event): void {
    if (this.showLinkSearch && !this.elementRef.nativeElement.contains(event.target)) {
      this.showLinkSearch = false;
      this.linkSearchQuery = "";
      this.linkSearchResults = [];
    }
  }

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.showLinkSearch,
    (event) => this.onDocumentClick(event),
  );

  constructor(
    private taskService: TaskService,
    public evaService: EvaService,
    private cdr: ChangeDetectorRef,
    private evaCompanyService: EvaCompanyService,
  ) {}

  ngOnInit() {
    this.resetForm();
    this.initLinkSearch();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Quand visible passe à true depuis le parent, reset + focus
    if (changes["visible"] && this.visible && !changes["visible"].firstChange) {
      this.resetForm();
      this.focusTitle();
    }
  }

  /** Ouvre le formulaire inline (depuis le trigger interne) */
  open() {
    this.visible = true;
    this.visibleChange.emit(true);
    this.resetForm();
    this.focusTitle();
  }

  /** Réinitialise le formulaire avec les valeurs par défaut */
  resetForm() {
    this.title = "";
    this.description = "";
    this.dueDate = this.defaultDate ? new Date(this.defaultDate) : null;
    this.plannedStartDate = null;
    this.dateMode = this.defaultDate ? "deadline" : "none";
    this.urgency = TaskUrgency.NORMAL;
    this.isPrivate = false;
    const assignee = this.defaultAssignee ?? this.buildCurrentEmployeeAssignee();
    this.assignedTo = assignee ? [assignee] : [];
    this.activeObjectsLinks = this.defaultObjectsLinks ? [...this.defaultObjectsLinks] : [];
    this.titleError = "";
    this.saving = false;
    this.showDescription = false;
  }

  /** Construit l'assigné courant depuis la session (utilisé comme valeur par défaut) */
  private buildCurrentEmployeeAssignee(): AssigneeItem | null {
    return defaultAssigneeFor(
      this.evaCompanyService.getCurrentEvaEmployee(),
      this.evaCompanyService.getCurrentEvaCompany()?.eva_company_id,
    );
  }

  /**
   * Focus le champ titre tout de suite : un délai laissait partir les premières frappes
   * dans la recherche de la page (EP-1120). Repli au frame suivant si la vue n'est pas encore rendue.
   */
  private focusTitle() {
    this.cdr.detectChanges();
    const input = this.titleInput?.inputElement?.nativeElement;
    if (input) {
      input.focus();
      return;
    }
    requestAnimationFrame(() => this.titleInput?.inputElement?.nativeElement?.focus());
  }

  /** Validation du formulaire */
  validate(): boolean {
    this.titleError = "";
    if (!this.title || this.title.trim() === "") {
      this.titleError = "Le nom est obligatoire";
      return false;
    }
    return true;
  }

  /** Soumission du formulaire */
  submit() {
    if (!this.validate()) return;
    if (this.saving) return;

    this.saving = true;

    const payload: any = {
      title: this.title.trim(),
      description: this.description.trim(),
      urgency: this.urgency,
      status: "TODO",
      assigned_to: this.assignedTo.map((a) =>
        a.type === "employee" ? { type: "employee", employee_id: a.employee_id } : { type: "external", name: a.name },
      ),
      objects_links: this.activeObjectsLinks,
      is_private: this.isPrivate,
    };

    if (this.dueDate) {
      payload.due_date = this.dueDate;
    }
    if (this.plannedStartDate) {
      payload.planned_start_date = this.plannedStartDate;
    }

    this.taskService
      .createTask(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (task: Task) => {
          this.evaService.showSuccess("Tâche créée");
          this.saving = false;
          this.taskCreated.emit(task);

          if (this.stayOpenAfterCreate) {
            // Mode "rester ouvert" : reset des champs saisis, focus sur le titre
            this.resetForm();
            this.focusTitle();
          } else {
            // Comportement par défaut : fermer le formulaire
            this.visible = false;
            this.visibleChange.emit(false);
            this.resetForm();
          }
        },
        error: () => {
          this.evaService.showError("Erreur lors de la création de la tâche", "createTask", "");
          this.saving = false;
        },
      });
  }

  onDateModeChange(mode: "none" | "deadline" | "range"): void {
    this.dateMode = mode;
    if (mode === "none") {
      this.dueDate = null;
      this.plannedStartDate = null;
    } else if (mode === "deadline") {
      this.plannedStartDate = null;
    }
  }

  onRangeDateChange(value: Date | Date[] | null): void {
    if (Array.isArray(value)) {
      this.plannedStartDate = value[0] || null;
      this.dueDate = value[1] || null;
    } else {
      this.plannedStartDate = null;
      this.dueDate = null;
    }
  }

  get rangeDateValue(): Date[] | null {
    if (this.plannedStartDate || this.dueDate) {
      return [this.plannedStartDate, this.dueDate].filter(Boolean) as Date[];
    }
    return null;
  }

  /** Icône PrimeIcons selon le type d'objet lié */
  get objectLinkIcon(): string {
    if (!this.activeObjectsLinks?.length) return "pi-link";
    const type = this.activeObjectsLinks[0].object_type?.toUpperCase();
    if (type === "CUSTOMER") return "pi-building";
    if (type === "OFFER") return "pi-file";
    if (type === "PROJECT") return "pi-hammer";
    if (type === "INVOICE") return "pi-receipt";
    if (type === "INTERVENTION") return "pi-wrench";
    return "pi-link";
  }

  /** Label de l'objet lié (premier élément) */
  get objectLinkLabel(): string {
    return this.activeObjectsLinks?.[0]?.object_name || "";
  }

  /** True si au moins un objet lié avec un nom est présent */
  get hasObjectLink(): boolean {
    return !!(this.activeObjectsLinks?.length && this.activeObjectsLinks[0]?.object_name);
  }

  get privateTagPreset(): TagPreset {
    return { label: "Privée", severity: "secondary", icon: "heroLockClosed" };
  }

  togglePrivate(): void {
    this.isPrivate = !this.isPrivate;
    // Le bouton est remplacé par l'étiquette (et inversement) : garder le focus pour continuer au clavier
    setTimeout(() =>
      this.elementRef.nativeElement
        .querySelector(".task-inline-create__private eva-tag, .task-inline-create__private button")
        ?.focus(),
    );
  }

  /** Retire le lien objet (dismiss du chip contextuel) */
  removeObjectLink(objectType?: string, objectId?: string): void {
    if (objectType && objectId) {
      this.activeObjectsLinks = this.activeObjectsLinks.filter(
        (l) => !(l.object_type === objectType && l.object_id === objectId),
      );
    } else {
      this.activeObjectsLinks = [];
    }
  }

  private initLinkSearch(): void {
    this.linkSearch$
      .pipe(
        debounceTime(300),
        switchMap((query) => {
          if (query.length < 2) {
            this.linkSearchResults = [];
            this.isSearchingLinks = false;
            return EMPTY;
          }
          this.isSearchingLinks = true;
          return this.noteService.searchAttachments(query);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        // Réponse asynchrone sous des parents OnPush (onglet Suivi) : signaler le changement
        next: (groups) => {
          this.linkSearchResults = groups || [];
          this.isSearchingLinks = false;
          this.cdr.markForCheck();
          this.revealLinkDropdown();
        },
        error: () => {
          this.linkSearchResults = [];
          this.isSearchingLinks = false;
          this.cdr.markForCheck();
        },
      });
  }

  toggleLinkSearch(): void {
    this.showLinkSearch = !this.showLinkSearch;
    if (!this.showLinkSearch) {
      this.linkSearchQuery = "";
      this.linkSearchResults = [];
      return;
    }
    this.revealLinkDropdown();
  }

  /** Dans un panneau qui défile (onglet Suivi), amener le menu dans la zone visible */
  private revealLinkDropdown(): void {
    setTimeout(() =>
      this.elementRef.nativeElement
        .querySelector(".task-inline-create__link-dropdown")
        ?.scrollIntoView({ block: "nearest" }),
    );
  }

  onLinkSearch(value: string): void {
    this.linkSearchQuery = value;
    this.linkSearch$.next(value);
  }

  addObjectLink(objectType: string, objectId: string, label: string): void {
    const exists = this.activeObjectsLinks.some((l) => l.object_type === objectType && l.object_id === objectId);
    if (exists) return;
    this.activeObjectsLinks = [
      ...this.activeObjectsLinks,
      { object_type: objectType, object_id: objectId, object_name: label },
    ];
    this.showLinkSearch = false;
    this.linkSearchQuery = "";
    this.linkSearchResults = [];
  }

  /** Annulation : ferme le formulaire et notifie le parent */
  cancel() {
    this.resetForm();
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();
  }

  /** Gestion des raccourcis clavier sur le titre */
  onTitleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.submit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      this.cancelOrKeepDraft(event);
    }
  }

  /** Échap garde un brouillon commencé (on quitte juste le champ), ferme un formulaire vide */
  private cancelOrKeepDraft(event: KeyboardEvent): void {
    if (this.title?.trim()) {
      (event.target as HTMLElement | null)?.blur();
      return;
    }
    this.cancel();
  }

  /** Gestion Escape sur la description */
  onDescriptionKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.cancelOrKeepDraft(event);
    }
  }
}
